import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './ScheduledJobs.css';
import axiosInstance from '../../api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_CFG = {
  COMPLETED: { color: '#15803d', bg: '#dcfce7', icon: 'fa-check-circle' },
  FAILED:    { color: '#dc2626', bg: '#fee2e2', icon: 'fa-times-circle' },
  KILLED:    { color: '#ea580c', bg: '#fff7ed', icon: 'fa-ban' },
  HUNG:      { color: '#d97706', bg: '#fef3c7', icon: 'fa-clock' },
  RUNNING:   { color: '#2563eb', bg: '#dbeafe', icon: 'fa-spinner fa-spin' },
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const s   = (status || '').toUpperCase();
  const cfg = STATUS_CFG[s] || { color: '#64748b', bg: '#f1f5f9', icon: 'fa-question-circle' };
  return (
    <span className="sj-badge" style={{ color: cfg.color, background: cfg.bg }}>
      <i className={`fas ${cfg.icon}`} />
      {s || t('ScheduledJobs.statusUnknown')}
    </span>
  );
}

function formatDuration(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000)  return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function TestBar({ passed, failed, total }) {
  if (!total) return <span className="sj-no-tests">—</span>;
  const pct = Math.round((passed / total) * 100);
  return (
    <div className="sj-test-bar-wrap">
      <div className="sj-test-bar">
        <div className="sj-test-bar-pass" style={{ width: `${pct}%` }} />
      </div>
      <span className="sj-test-label">
        <span className="sj-pass">{passed}</span>
        <span className="sj-sep">/</span>
        <span className="sj-fail">{failed > 0 ? failed : total - passed}</span>
      </span>
    </div>
  );
}

// ── Add Schedule Modal ────────────────────────────────────────────────────────

function AddScheduleModal({ orgs, onSave, onClose }) {
  const { t } = useTranslation();
  const CRON_PRESETS = [
    { label: t('ScheduledJobs.cronPresetDailyMidnight'),  value: '0 0 0 * * *' },
    { label: t('ScheduledJobs.cronPresetDaily2Am'),       value: '0 0 2 * * *' },
    { label: t('ScheduledJobs.cronPresetEverySunday'),    value: '0 0 0 * * SUN' },
    { label: t('ScheduledJobs.cronPresetEveryMonday6Am'), value: '0 0 6 * * MON' },
    { label: t('ScheduledJobs.cronPresetEvery6Hours'),    value: '0 0 */6 * * *' },
    { label: t('ScheduledJobs.cronPresetEveryHour'),      value: '0 0 * * * *' },
  ];
  const [orgId, setOrgId]           = useState('');
  const [cron, setCron]             = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const handleSave = async () => {
    if (!orgId) { setError(t('ScheduledJobs.validationSelectOrg')); return; }
    if (!cron.trim()) { setError(t('ScheduledJobs.validationEnterCron')); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(orgId, cron.trim());
      onClose();
    } catch (e) {
      setError(e.response?.data || e.message || t('ScheduledJobs.errorSaveSchedule'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sj-modal-overlay" onClick={onClose}>
      <div className="sj-modal" onClick={e => e.stopPropagation()}>
        <div className="sj-modal-header">
          <span className="sj-modal-title"><i className="fas fa-calendar-plus" /> {t('ScheduledJobs.addScheduleTitle')}</span>
          <button className="sj-modal-close" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="sj-modal-body">
          <label className="sj-label">{t('ScheduledJobs.organisationLabel')}</label>
          <select className="sj-input" value={orgId} onChange={e => setOrgId(e.target.value)}>
            <option value="">{t('ScheduledJobs.selectPlaceholder')}</option>
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{t('ScheduledJobs.orgOption', { name: o.name, id: o.id })}</option>
            ))}
          </select>

          <label className="sj-label" style={{ marginTop: 14 }}>{t('ScheduledJobs.cronExpressionLabel')}</label>
          <div className="sj-cron-row">
            <input
              className="sj-input sj-cron-input"
              placeholder="0 0 2 * * *"
              value={cron}
              onChange={e => setCron(e.target.value)}
            />
          </div>
          <div className="sj-preset-row">
            {CRON_PRESETS.map(p => (
              <button key={p.value} className="sj-preset-btn" onClick={() => setCron(p.value)}>
                {p.label}
              </button>
            ))}
          </div>
          {cron && (
            <div className="sj-cron-preview">
              <i className="fas fa-clock" /> <code>{cron}</code>
            </div>
          )}
          {error && <div className="sj-modal-error"><i className="fas fa-exclamation-circle" /> {error}</div>}
        </div>
        <div className="sj-modal-footer">
          <button className="sj-modal-cancel-btn" onClick={onClose}>{t('ScheduledJobs.cancel')}</button>
          <button className="sj-modal-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <><i className="fas fa-circle-notch fa-spin" /> {t('ScheduledJobs.saving')}</> : t('ScheduledJobs.activateSchedule')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ScheduledJobsPage() {
  const { t } = useTranslation();
  const [tab, setTab]               = useState('schedules');

  // Schedules tab
  const [configs,       setConfigs]       = useState([]);
  const [orgs,          setOrgs]          = useState([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [deletingOrgId, setDeletingOrgId] = useState(null);

  // History tab
  const [jobs,         setJobs]         = useState([]);
  const [histLoading,  setHistLoading]  = useState(true);
  const [histError,    setHistError]    = useState(null);
  const [filter,       setFilter]       = useState('');
  const [search,       setSearch]       = useState('');
  const [cancelling,   setCancelling]   = useState(null);
  const [deletingId,   setDeletingId]   = useState(null);
  const [page,         setPage]         = useState(0);
  const [lastRefresh,  setLastRefresh]  = useState(null);
  const pageSize = 15;

  // ── Fetchers ──────────────────────────────────────────────────────────────

  const loadConfigs = useCallback(async () => {
    setConfigLoading(true);
    try {
      const [cfgRes, orgRes] = await Promise.all([
        axiosInstance.get('/scheduler/configurations'),
        axiosInstance.get('/scheduler/organizations'),
      ]);
      setConfigs(cfgRes.data || []);
      setOrgs(orgRes.data || []);
    } catch { /* silent */ }
    finally { setConfigLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    setHistError(null);
    try {
      const res = await axiosInstance.get('/scheduler');
      setJobs(res.data || []);
      setLastRefresh(new Date());
    } catch (e) {
      setHistError(e.message);
    } finally {
      setHistLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); loadHistory(); }, [loadConfigs, loadHistory]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleAddSchedule = async (orgId, cronExpression) => {
    await axiosInstance.post('/scheduler/configurations', { orgId: String(orgId), cronExpression });
    await loadConfigs();
  };

  const handleDeleteConfig = async (orgId, orgName) => {
    if (!window.confirm(t('ScheduledJobs.confirmRemoveSchedule', { orgName }))) return;
    setDeletingOrgId(orgId);
    try {
      await axiosInstance.delete(`/scheduler/configurations/${orgId}`);
      setConfigs(prev => prev.filter(c => c.orgId !== orgId));
    } catch (e) {
      alert(t('ScheduledJobs.alertRemoveScheduleFailed', { message: e.message }));
    } finally {
      setDeletingOrgId(null);
    }
  };

  const handleCancelJob = async (job) => {
    if (!window.confirm(t('ScheduledJobs.confirmCancelJob', { orgName: job.orgName || t('ScheduledJobs.orgFallback', { orgId: job.orgId }) }))) return;
    setCancelling(job.id);
    try {
      await axiosInstance.post(`/scheduler/cancel?orgId=${job.orgId}`);
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: 'KILLED' } : j));
    } catch (e) {
      alert(t('ScheduledJobs.alertCancelFailed', { message: e.message }));
    } finally {
      setCancelling(null);
    }
  };

  const handleDeleteHistory = async (job) => {
    if (!window.confirm(t('ScheduledJobs.confirmDeleteHistory', { id: job.id }))) return;
    setDeletingId(job.id);
    try {
      await axiosInstance.delete(`/scheduler/${job.id}`);
      setJobs(prev => prev.filter(j => j.id !== job.id));
    } catch (e) {
      alert(t('ScheduledJobs.alertDeleteFailed', { message: e.message }));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const counts = jobs.reduce((acc, j) => {
    const s = (j.status || '').toUpperCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const filtered = jobs.filter(j => {
    const matchFilter = !filter || (j.status || '').toUpperCase() === filter;
    const matchSearch = !search ||
      (j.jobName  || '').toLowerCase().includes(search.toLowerCase()) ||
      (j.orgName  || '').toLowerCase().includes(search.toLowerCase()) ||
      String(j.orgId || '').includes(search);
    return matchFilter && matchSearch;
  }).sort((a, b) => new Date(b.startTime || 0) - new Date(a.startTime || 0));

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageSlice  = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const liveCount = configs.filter(c => c.live).length;

  return (
    <div className="sj-page">

      {/* ── Stats row ─────────────────────────────────────────── */}
      <div className="sj-stats">
        {[
          { label: t('ScheduledJobs.statActiveSchedules'), value: liveCount,              accent: '#7c3aed' },
          { label: t('ScheduledJobs.statTotalRuns'),       value: jobs.length,            accent: '#2563eb' },
          { label: t('ScheduledJobs.statCompleted'),       value: counts.COMPLETED || 0,  accent: '#15803d' },
          { label: t('ScheduledJobs.statFailed'),          value: counts.FAILED    || 0,  accent: '#dc2626' },
          { label: t('ScheduledJobs.statRunningNow'),      value: counts.RUNNING   || 0,  accent: '#0891b2' },
          { label: t('ScheduledJobs.statKilledHung'),      value: (counts.KILLED || 0) + (counts.HUNG || 0), accent: '#ea580c' },
        ].map(s => (
          <div key={s.label} className="sj-stat-card" style={{ borderTopColor: s.accent }}>
            <div className="sj-stat-value" style={{ color: s.accent }}>{s.value}</div>
            <div className="sj-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tab bar ───────────────────────────────────────────── */}
      <div className="sj-tab-bar">
        <button className={`sj-tab${tab === 'schedules' ? ' sj-tab-active' : ''}`}
                onClick={() => setTab('schedules')}>
          <i className="fas fa-calendar-alt" /> {t('ScheduledJobs.tabSchedules')}
          {configs.length > 0 && <span className="sj-tab-count">{configs.length}</span>}
        </button>
        <button className={`sj-tab${tab === 'history' ? ' sj-tab-active' : ''}`}
                onClick={() => setTab('history')}>
          <i className="fas fa-history" /> {t('ScheduledJobs.tabJobHistory')}
          {jobs.length > 0 && <span className="sj-tab-count">{jobs.length}</span>}
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════
          TAB: SCHEDULES
      ═══════════════════════════════════════════════════════ */}
      {tab === 'schedules' && (
        <>
          <div className="sj-toolbar">
            <div className="sj-section-title">
              <i className="fas fa-cog" /> {t('ScheduledJobs.organisationSchedules')}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="sj-refresh-btn" onClick={loadConfigs} disabled={configLoading} title={t('ScheduledJobs.refresh')}>
                <i className={`fas fa-sync-alt${configLoading ? ' fa-spin' : ''}`} />
              </button>
              <button className="sj-add-btn" onClick={() => setShowAddModal(true)}>
                <i className="fas fa-plus" /> {t('ScheduledJobs.addScheduleTitle')}
              </button>
            </div>
          </div>

          <div className="sj-table-wrap">
            {configLoading ? (
              <div className="sj-state-center">
                <i className="fas fa-circle-notch fa-spin sj-spin-icon" />
                <span>{t('ScheduledJobs.loadingSchedules')}</span>
              </div>
            ) : configs.length === 0 ? (
              <div className="sj-empty">
                <div className="sj-empty-icon"><i className="fas fa-calendar-times" /></div>
                <div className="sj-empty-title">{t('ScheduledJobs.emptySchedulesTitle')}</div>
                <div className="sj-empty-sub">
                  {t('ScheduledJobs.emptySchedulesSub')}
                </div>
                <button className="sj-add-btn" style={{ marginTop: 16 }} onClick={() => setShowAddModal(true)}>
                  <i className="fas fa-plus" /> {t('ScheduledJobs.addFirstSchedule')}
                </button>
              </div>
            ) : (
              <table className="sj-table">
                <thead>
                  <tr>
                    <th>{t('ScheduledJobs.colOrganisation')}</th>
                    <th>{t('ScheduledJobs.colCronExpression')}</th>
                    <th>{t('ScheduledJobs.colLastRun')}</th>
                    <th>{t('ScheduledJobs.colStatus')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map(cfg => (
                    <tr key={cfg.orgId}>
                      <td>
                        <span className="sj-org-name">{cfg.orgName}</span>
                        <span className="sj-job-id">{t('ScheduledJobs.idLabel', { id: cfg.orgId })}</span>
                      </td>
                      <td>
                        <code className="sj-cron">{cfg.cronExpression}</code>
                      </td>
                      <td className="sj-date">{formatDateTime(cfg.lastExecution)}</td>
                      <td>
                        {cfg.live ? (
                          <span className="sj-badge" style={{ color: '#7c3aed', background: '#f5f3ff' }}>
                            <i className="fas fa-circle" style={{ fontSize: '0.5rem' }} /> {t('ScheduledJobs.statusLive')}
                          </span>
                        ) : (
                          <span className="sj-badge" style={{ color: '#64748b', background: '#f1f5f9' }}>
                            <i className="fas fa-moon" /> {t('ScheduledJobs.statusPendingRestart')}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="sj-del-btn"
                          title={t('ScheduledJobs.removeScheduleTitle')}
                          disabled={deletingOrgId === cfg.orgId}
                          onClick={() => handleDeleteConfig(cfg.orgId, cfg.orgName)}
                        >
                          {deletingOrgId === cfg.orgId
                            ? <i className="fas fa-circle-notch fa-spin" />
                            : <i className="fas fa-trash-alt" />
                          }
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════
          TAB: JOB HISTORY
      ═══════════════════════════════════════════════════════ */}
      {tab === 'history' && (
        <>
          {/* ── Toolbar ──────────────────────────────────────── */}
          <div className="sj-toolbar">
            <div className="sj-search-wrap">
              <i className="fas fa-search sj-search-icon" />
              <input
                className="sj-search"
                placeholder={t('ScheduledJobs.searchPlaceholder')}
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
              />
              {search && (
                <button className="sj-search-clear" onClick={() => setSearch('')}>
                  <i className="fas fa-times" />
                </button>
              )}
            </div>

            <div className="sj-filter-group">
              {['', 'RUNNING', 'COMPLETED', 'FAILED', 'KILLED', 'HUNG'].map(s => (
                <button
                  key={s}
                  className={`sj-filter-btn${filter === s ? ' sj-filter-btn-active' : ''}`}
                  onClick={() => { setFilter(s); setPage(0); }}
                >
                  {s || t('ScheduledJobs.filterAll')}
                </button>
              ))}
            </div>

            <button className="sj-refresh-btn" onClick={loadHistory} disabled={histLoading} title={t('ScheduledJobs.refresh')}>
              <i className={`fas fa-sync-alt${histLoading ? ' fa-spin' : ''}`} />
            </button>

            {lastRefresh && (
              <span className="sj-last-refresh">
                {t('ScheduledJobs.updatedAt', { time: lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) })}
              </span>
            )}
          </div>

          {histError && (
            <div className="sj-error">
              <i className="fas fa-exclamation-circle" /> {histError}
              <button className="sj-error-retry" onClick={loadHistory}>{t('ScheduledJobs.retry')}</button>
            </div>
          )}

          <div className="sj-table-wrap">
            {histLoading ? (
              <div className="sj-state-center">
                <i className="fas fa-circle-notch fa-spin sj-spin-icon" />
                <span>{t('ScheduledJobs.loadingHistory')}</span>
              </div>
            ) : pageSlice.length === 0 ? (
              <div className="sj-empty">
                <div className="sj-empty-icon"><i className="fas fa-calendar-alt" /></div>
                <div className="sj-empty-title">{t('ScheduledJobs.emptyHistoryTitle')}</div>
                <div className="sj-empty-sub">
                  {t('ScheduledJobs.emptyHistorySub')}
                  {filter || search ? t('ScheduledJobs.emptyHistoryClearFilters') : ''}
                </div>
              </div>
            ) : (
              <table className="sj-table">
                <thead>
                  <tr>
                    <th>{t('ScheduledJobs.colJob')}</th>
                    <th>{t('ScheduledJobs.colOrganisation')}</th>
                    <th>{t('ScheduledJobs.colStatus')}</th>
                    <th>{t('ScheduledJobs.colStarted')}</th>
                    <th>{t('ScheduledJobs.colEnded')}</th>
                    <th>{t('ScheduledJobs.colDuration')}</th>
                    <th>{t('ScheduledJobs.colTests')}</th>
                    <th>{t('ScheduledJobs.colCron')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.map(job => {
                    const isRunning = (job.status || '').toUpperCase() === 'RUNNING';
                    return (
                      <tr key={job.id} className={isRunning ? 'sj-row-running' : ''}>
                        <td>
                          <span className="sj-job-name">{job.jobName || '—'}</span>
                          <span className="sj-job-id">#{job.id}</span>
                        </td>
                        <td>
                          <span className="sj-org-name">{job.orgName || '—'}</span>
                          <span className="sj-job-id">{job.orgId != null ? t('ScheduledJobs.idLabel', { id: job.orgId }) : t('ScheduledJobs.idLabel', { id: '—' })}</span>
                        </td>
                        <td><StatusBadge status={job.status} /></td>
                        <td className="sj-date">{formatDateTime(job.startTime)}</td>
                        <td className="sj-date">{formatDateTime(job.endTime)}</td>
                        <td className="sj-dur">{formatDuration(job.durationMs)}</td>
                        <td>
                          <TestBar
                            passed={job.passedTests}
                            failed={job.failedTests}
                            total={job.totalTests}
                          />
                        </td>
                        <td>
                          {job.cronExpression
                            ? <code className="sj-cron">{job.cronExpression}</code>
                            : <span className="sj-no-cron">—</span>
                          }
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            {isRunning && (
                              <button
                                className="sj-cancel-btn"
                                onClick={() => handleCancelJob(job)}
                                disabled={cancelling === job.id}
                                title={t('ScheduledJobs.cancelJobTitle')}
                              >
                                {cancelling === job.id
                                  ? <i className="fas fa-circle-notch fa-spin" />
                                  : <i className="fas fa-stop-circle" />
                                }
                              </button>
                            )}
                            {!isRunning && (
                              <button
                                className="sj-del-btn"
                                onClick={() => handleDeleteHistory(job)}
                                disabled={deletingId === job.id}
                                title={t('ScheduledJobs.deleteHistoryTitle')}
                              >
                                {deletingId === job.id
                                  ? <i className="fas fa-circle-notch fa-spin" />
                                  : <i className="fas fa-trash-alt" />
                                }
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination ──────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="sj-pagination">
              <button className="sj-page-btn" onClick={() => setPage(0)}         disabled={page === 0}><i className="fas fa-angle-double-left" /></button>
              <button className="sj-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}><i className="fas fa-angle-left" /></button>
              <span className="sj-page-info">{t('ScheduledJobs.pageInfo', { page: page + 1, totalPages, count: filtered.length })}</span>
              <button className="sj-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}><i className="fas fa-angle-right" /></button>
              <button className="sj-page-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}><i className="fas fa-angle-double-right" /></button>
            </div>
          )}
        </>
      )}

      {/* ── Add Schedule Modal ────────────────────────────────── */}
      {showAddModal && (
        <AddScheduleModal
          orgs={orgs}
          onSave={handleAddSchedule}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}
